import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormHelperText,
  Stack,
  Container,
  Typography,
} from '@mui/material';
import { DesktopDateRangePicker } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { useField } from 'formik';
import TextFieldWrapper from '../../common/TextFieldWrapper/TextFieldWrapper';
import type { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';

interface DateRangePickerWrapperProps {
  description: string;
  formikDateRange?: string;
  datesClass?: string;
  formikStartDate: string;
  formikEndDate: string;
  isEdit?: string;
  title?: string;
  validateDateRange?: (
    startDate: moment.Moment,
    endDate: moment.Moment
  ) => boolean;
}

const DateRangePickerWrapper = ({
  description,
  formikDateRange = 'dateRangeField',
  datesClass,
  formikStartDate,
  formikEndDate,
  isEdit,
  title,
  validateDateRange,
}: DateRangePickerWrapperProps) => {
  const [startDateField, , startDateFieldHelpers] = useField(formikStartDate);
  const [endDateField, , endDateFieldHelpers] = useField(formikEndDate);
  const [dateRangeField, dateRangeFieldMeta, dateRangeFieldHelpers] =
    useField(formikDateRange);
  const [minDate, setMinDate] = useState(moment().utc());

  const originalEndDateRef = useRef(moment(endDateField.value).utc(true));
  const calledOnce = useRef(false);

  useEffect(() => {
    if (!calledOnce.current && startDateField.value !== '') {
      const initialDate = moment(startDateField.value).utc(true);
      initialDate ? setMinDate(initialDate) : setMinDate(minDate);
      calledOnce.current = true;
    }
  }, [minDate, setMinDate, startDateField.value, isEdit]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const popup = document.body.querySelector('.MuiPickersPopper-root');

      if (popup) {
        const invalidLicenseMessageDiv = [
          ...popup.querySelectorAll('div'),
        ].filter((div) =>
          div.innerText.includes('MUI X: Invalid license key')
        )[2];
        if (invalidLicenseMessageDiv) {
          invalidLicenseMessageDiv.style.visibility = 'hidden';
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const preventKeyboardInput = (e: any) => {
    e.preventDefault();
  };
  const handleDateRangeChange = (range: DateRange<moment.Moment>) => {
    const newStartDate = range[0];
    const newEndDate = range[1];

    if (
      !newStartDate ||
      !newEndDate ||
      !newStartDate.isValid() ||
      !newEndDate.isValid()
    ) {
      return;
    }

    if (validateDateRange && !validateDateRange(newStartDate, newEndDate)) {
      dateRangeFieldHelpers.setValue([
        moment(startDateField.value),
        originalEndDateRef.current,
      ]);
      startDateFieldHelpers.setValue(
        moment(startDateField.value).toDate().getTime()
      );
      endDateFieldHelpers.setValue(
        originalEndDateRef.current.toDate().getTime()
      );
      return;
    }

    dateRangeFieldHelpers.setValue([
      moment(range[0]).toDate().getTime(),
      moment(range[1]).endOf('day').toDate().getTime(),
    ]);
    startDateFieldHelpers.setValue(moment(range[0]).toDate().getTime());
    endDateFieldHelpers.setValue(
      moment(range[1]).endOf('day').toDate().getTime()
    );
  };
  return (
    <Container disableGutters>
      <Typography variant={'h3'} gutterBottom>
        {title}
      </Typography>
      <Typography variant={'body1'} sx={{ marginBottom: 2 }}>
        {description}
      </Typography>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale='en-US'
        localeText={{
          start: 'Start Date',
          end: 'End Date',
        }}
      >
        <Stack sx={{ gap: '24px' }} className={datesClass}>
          <DesktopDateRangePicker
            value={dateRangeField.value}
            minDate={minDate}
            onChange={(value) => {
              dateRangeFieldHelpers.setValue(value);
              handleDateRangeChange(value);
            }}
            renderInput={(startProps, endProps) => (
              <React.Fragment>
                <TextFieldWrapper
                  onKeyDown={preventKeyboardInput}
                  className='input-text'
                  {...startProps}
                />
                <Box sx={{ mx: 2 }} className='range-picker-input-space'> </Box>
                <TextFieldWrapper
                  onKeyDown={preventKeyboardInput}
                  className='input-text'
                  {...endProps}
                />
              </React.Fragment>
            )}
          />
          <FormHelperText error={true}>
            {dateRangeFieldMeta.error}
          </FormHelperText>
        </Stack>
      </LocalizationProvider>
    </Container>
  );
};

export { DateRangePickerWrapper };
